import { PRICES } from './prices.ts';
import { advance, content, dayOf, earn, hourOf, message, progress, spend, type LifeEvent, type LifeState, money } from './life.ts';
import { Rng } from './rng.ts';
import { randomName, TRAIT_AXES } from './spawn.ts';
import { abroad, countryOfHood, homeCountry, type Country } from './travel.ts';
import { takeIntoCustody } from './custody.ts';

/**
 * The local colour of a chance: the same temptation, told where you are right now (a Lagos life in
 * Tokyo gets Tokyo's version). Amounts stay in the sim's units; only names, places and voices change.
 */
interface Flavour {
  charity: [from: string, text: string, done: string]; blessing: string;
  packageFrom: string; packageWho: string; packageTo: string; packageDrop: string; packageRobbed: string;
  carRoute: string; carRoad: string; carDone: string;
  fightAt: string; fightWho: string;
  betFrom: string; betLine: (stake: string) => string; betWin: string;
  please: string; lieLine: string;
  party: [place: string, text: string, done: string];
  bribeTag: string; lender: string; police: string;
  shop: string; sideJobs: Record<'technical' | 'creativity' | 'cooking' | 'music' | 'academics', string>;
}
const FLAVOUR: Record<Country, Flavour> = {
  nigeria: {
    charity: ['Mrs Okafor', 'The church down the road is running a food drive on Saturday. They need someone to organise the volunteers. People said you\'re reliable.', 'Two hundred families go home with rice and oil.'], blessing: '"God will bless you."',
    packageFrom: 'A man at the junction', packageWho: 'A man you\'ve seen around the junction', packageTo: 'Apapa', packageDrop: 'A gate in Apapa', packageRobbed: 'Two boys on an okada were waiting at the drop.',
    carRoute: 'from Ikeja to Badagry', carRoad: 'Badagry Expressway', carDone: 'Three hours of dark expressway, every checkpoint a held breath. Nobody stops you.',
    fightAt: 'Outside the viewing centre', fightWho: 'A big guy outside the viewing centre',
    betFrom: 'Your guy at the bet shop', betLine: (x) => `"Tonight's game is a banker. Sure thing. Put ${x} on it and you go triple am."`, betWin: 'Last-minute goal.',
    please: ' Abeg.', lieLine: '"Yes now, he slept here."',
    party: ['Lekki', 'There\'s a party in Lekki tonight. Big boys, big cars, people you\'d never meet otherwise. "Just come. Dress well."', 'Loud music, a pool nobody swims in'],
    bribeTag: ', abi?', lender: 'Alhaji', police: 'Police stopped you at a checkpoint. You spent the night in a cell at the station.',
    shop: 'The provisions shop', sideJobs: { technical: 'fix a generator that won\'t start', creativity: 'design flyers for a shop opening', cooking: 'cook for a naming ceremony', music: 'play keys at a wedding reception', academics: 'tutor a boy for his WAEC maths' },
  },
  france: {
    charity: ['Madame Lefèvre', 'The Restos du Cœur are collecting food on Saturday and need someone to run the volunteers. Someone said you\'re reliable.', 'Two hundred families go home with a full bag.'], blessing: '"Merci. Vraiment. I won\'t forget it."',
    packageFrom: 'A guy outside the bar-tabac', packageWho: 'A guy you\'ve seen outside the bar-tabac', packageTo: 'a garage in Saint-Denis', packageDrop: 'A garage in Saint-Denis', packageRobbed: 'Two guys on a scooter were waiting at the drop.',
    carRoute: 'from Paris down to Marseille overnight', carRoad: 'the A7 péage', carDone: 'Eight hours of autoroute, every péage a held breath. Nobody stops you.',
    fightAt: 'Outside the bar-tabac', fightWho: 'A big guy outside the bar-tabac',
    betFrom: 'Your guy at the PMU bar', betLine: (x) => `"The horse in the third at Vincennes can't lose. Put ${x} on it, it pays three to one."`, betWin: 'A nose at the line.',
    please: ' Please.', lieLine: '"Oui, he slept on my sofa."',
    party: ['a rooftop in the 16th', 'There\'s a party on a rooftop in the 16th tonight. Money, models, people you\'d never meet otherwise. "Just come. Dress well."', 'Champagne, the Eiffel Tower sparkling on the hour'],
    bribeTag: ', d\'accord?', lender: 'Le Banquier', police: 'The police stopped you. You spent the night in garde à vue.',
    shop: 'The épicerie', sideJobs: { technical: 'fix a neighbour\'s laptop', creativity: 'design the menu for a new bistro', cooking: 'cater a christening lunch', music: 'play piano at a wedding reception', academics: 'tutor a girl for her bac maths' },
  },
  uae: {
    charity: ['Auntie Grace from church', 'The Iftar tents by the mosque in Al Karama feed workers every evening this month and need hands to serve. Someone said you’re reliable.', 'Six hundred plates of rice and lamb, served before the sun goes down.'], blessing: '"Barakallahu feek. God will reward you."',
    packageFrom: 'A guy who hangs around the car park behind the souk', packageWho: 'A guy you’ve seen behind the souk', packageTo: 'a warehouse in Al Quoz', packageDrop: 'A warehouse in Al Quoz', packageRobbed: 'The warehouse was empty. Two men in a pickup were waiting.',
    carRoute: 'from Dubai to Abu Dhabi overnight', carRoad: 'Sheikh Zayed Road', carDone: 'An hour and a half of empty highway and speed cameras. Nobody stops you.',
    fightAt: 'Outside a shisha place in Deira', fightWho: 'A big guy outside a shisha place in Deira',
    betFrom: 'A guy on a group chat', betLine: (x) => `"The camel race on Friday, I know the trainer. Put ${x} on it, it pays three to one."`, betWin: 'Your camel wins by a neck.',
    please: ' Please, habibi.', lieLine: '"Yes, he was at my flat all night."',
    party: ['a yacht party at the Marina', 'There’s a party on a yacht in the Marina tonight. Promoters, influencers, people who are someone back home. "Just come. Dress sharp."', 'Music over the water, the towers lit up all round'],
    bribeTag: ', okay?', lender: 'The cheque guy', police: 'Dubai Police stopped you. You spent the night at the station, and they kept your passport for a week.',
    shop: 'The corner grocery', sideJobs: { technical: 'set up the Wi-Fi in a villa in Jumeirah', creativity: 'design the menu for a new karak tea place', cooking: 'cook for a family’s Friday lunch', music: 'play at a hotel brunch', academics: 'tutor a boy for his IGCSE maths' },
  },
  italy: {
    charity: ['Signora Bellini', 'The parish food bank by Santo Spirito is collecting on Saturday and needs someone to run the volunteers. Someone said you\'re reliable.', 'Two hundred families go home with a full bag.'], blessing: '"Grazie di cuore. I won\'t forget it."',
    packageFrom: 'A guy outside the bar on the piazza', packageWho: 'A guy you\'ve seen outside the bar on the piazza', packageTo: 'a warehouse out past Prato', packageDrop: 'A warehouse out past Prato', packageRobbed: 'Two guys on a Vespa were waiting at the drop.',
    carRoute: 'from Florence down to Naples overnight', carRoad: 'the A1 autostrada', carDone: 'Five hours of autostrada, every toll booth a held breath. Nobody stops you.',
    fightAt: 'Outside the bar on the piazza', fightWho: 'A big guy outside the bar on the piazza',
    betFrom: 'Your guy at the tabaccheria', betLine: (x) => `"Fiorentina at home on Sunday, can't lose. Put ${x} on it, it pays three to one."`, betWin: 'A header in the ninetieth minute.',
    please: ' Per favore.', lieLine: '"Sì, he slept on my sofa."',
    party: ['a villa in the hills', 'There\'s a party at a villa up in Fiesole tonight. Old money, artists, people you\'d never meet otherwise. "Just come. Dress well."', 'Prosecco on a terrace, the whole city lit up below'],
    bribeTag: ', va bene?', lender: 'Il Cravattaro', police: 'The Carabinieri stopped you. You spent the night at the station.',
    shop: 'The alimentari', sideJobs: { technical: 'fix a neighbour\'s laptop', creativity: 'paint the sign for a new trattoria', cooking: 'cook for a christening lunch', music: 'play guitar at a wedding dinner', academics: 'tutor a boy for his maturità maths' },
  },
  usa: {
    charity: ['Mrs Delgado', 'The food bank down the street is running a drive on Saturday and needs someone to run the volunteers. People said you\'re reliable.', 'Two hundred families go home with groceries.'], blessing: '"You\'re a lifesaver. Seriously."',
    packageFrom: 'A guy on the corner', packageWho: 'A guy you\'ve seen on the corner', packageTo: 'a warehouse across the river', packageDrop: 'A loading dock across the river', packageRobbed: 'Two guys in a car were waiting at the drop.',
    carRoute: 'to Philadelphia tonight', carRoad: 'the Turnpike', carDone: 'Two hours on the Turnpike, every set of headlights a held breath. Nobody stops you.',
    fightAt: 'Outside the sports bar', fightWho: 'A big guy outside the sports bar',
    betFrom: 'Your buddy with the betting app', betLine: (x) => `"Tonight's game is a lock. Put ${x} on the parlay, it pays triple."`, betWin: 'A buzzer-beater.',
    please: ' Please, man.', lieLine: '"Yeah, he crashed at my place."',
    party: ['a penthouse', 'There\'s a party in a penthouse tonight. Big money, people you\'d never meet otherwise. "Just come. Dress well."', 'A rooftop pool, a DJ'],
    bribeTag: ', okay?', lender: 'Rich', police: 'The cops picked you up. You spent the night in a holding cell.',
    shop: 'The bodega', sideJobs: { technical: 'fix a neighbour\'s laptop', creativity: 'design flyers for a new coffee shop', cooking: 'cater a backyard birthday party', music: 'play keys at a wedding reception', academics: 'tutor a kid for the SAT' },
  },
  china: {
    charity: ['Auntie Zhou', 'The neighbourhood committee needs volunteers for a food drive on Saturday. Someone said you\'re reliable.', 'Two hundred families go home with rice and oil.'], blessing: '"Thank you, thank you. I owe you."',
    packageFrom: 'A man at the night market', packageWho: 'A man you\'ve seen at the night market', packageTo: 'a warehouse outside the city', packageDrop: 'A warehouse outside the city', packageRobbed: 'Two men on an e-bike were waiting at the drop.',
    carRoute: 'out to Suzhou overnight', carRoad: 'the expressway', carDone: 'Three hours of expressway, every toll gate a held breath. Nobody stops you.',
    fightAt: 'Outside the KTV', fightWho: 'A drunk guy outside the KTV',
    betFrom: 'A friend with a betting group chat', betLine: (x) => `"Tonight's match is fixed, trust me. Put ${x} in, it pays three times."`, betWin: 'A goal in stoppage time.',
    please: ' Please.', lieLine: '"Yes, he was at mine last night."',
    party: ['a club on the Bund', 'There\'s a party at a club on the Bund tonight. Rich kids, sports cars, people you\'d never meet otherwise. "Just come. Dress well."', 'Bottles with sparklers, Pudong lit up across the river'],
    bribeTag: ', understood?', lender: 'Boss Wang', police: 'The police stopped you. You spent the night at the station.',
    shop: 'The corner shop', sideJobs: { technical: 'repair a neighbour\'s phone', creativity: 'design posters for a milk-tea shop opening', cooking: 'cook for a family banquet', music: 'play piano at a wedding banquet', academics: 'tutor a boy for the gaokao' },
  },
  japan: {
    charity: ['Mrs Sato', 'The neighbourhood association is running a food drive on Saturday and needs someone to organise the volunteers. People said you\'re reliable.', 'Two hundred families go home with rice and miso.'], blessing: '"Thank you. Really. I\'ll pay you back, I promise."',
    packageFrom: 'A man outside the pachinko parlour', packageWho: 'A man you\'ve seen outside the pachinko parlour', packageTo: 'Yokohama', packageDrop: 'A warehouse in Yokohama', packageRobbed: 'Two men on a scooter were waiting at the drop.',
    carRoute: 'down the expressway to Osaka overnight', carRoad: 'the Tomei Expressway', carDone: 'Six hours of expressway, every toll gate a held breath. Nobody stops you.',
    fightAt: 'Outside the izakaya', fightWho: 'A drunk salaryman outside the izakaya',
    betFrom: 'Your friend who plays the horses', betLine: (x) => `"There's a horse at Tokyo Racecourse that can't lose. Put ${x} on it, it pays three to one."`, betWin: 'A nose at the line.',
    please: ' Please.', lieLine: '"Yes, he stayed at mine."',
    party: ['a rooftop in Roppongi', 'There\'s a party on a rooftop in Roppongi tonight. Money, people you\'d never meet otherwise. "Just come. Dress well."', 'Tokyo Tower glowing orange over the rail'],
    bribeTag: ', I trust?', lender: 'Mr Kaneda', police: 'The police stopped you. You spent the night at the station.',
    shop: 'The little grocer\'s', sideJobs: { technical: 'fix a neighbour\'s laptop', creativity: 'design flyers for a new café', cooking: 'cook for a neighbourhood festival stall', music: 'play piano at a wedding reception', academics: 'tutor a girl for her entrance exams' },
  },
};
/** The country you're in right now (abroad: where the trip is; else home). */
export function hereCountry(s: LifeState): Country { const w = abroad(s); return w ? countryOfHood(w) : homeCountry(s); }
const F = (s: LifeState) => FLAVOUR[hereCountry(s)];
/** a gym day pass where you are (internal units): what one actually costs there, not a Lagos price converted */
const GYM: Record<Country, number> = { nigeria: 2_000, france: 12 * 1_700, usa: 25 * 1_550, china: 50 * 215, japan: 1_500 * 10, italy: 10 * 1_700, uae: 350 * 422 };
export function gymFee(s: LifeState): number { return GYM[hereCountry(s)]; }
/** what a Lagos-sized sum is worth to people here (a neighbour's thank-you, a package run, a bribe) */
const COST: Record<Country, number> = { nigeria: 1, france: 4, usa: 5, china: 2, japan: 4, italy: 3.5, uae: 3 };
/** A Lagos-sized amount at your HOME country's prices (what a sum is worth where you live, wherever you are today). */
export function homePrice(s: LifeState, n: number, to = 500): number {
  const k = COST[homeCountry(s)];
  if (k === 1) return Math.round(n / to) * to;
  const rate = s.card.currency.rate ?? 1, v = (n * k) / rate;
  let step = 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1);
  if (v / step > 50) step *= 5;
  return Math.round(v / step) * step * rate;
}
/** A Lagos-sized amount `n` at this place's prices, rounded to a figure people say (in the currency this life sees). */
export function localPrice(s: LifeState, n: number, to = 500): number { return local(s, n * PRICES, to); }
/** a Lagos-sized sum at this place's money that ISN'T a price (what you earn, a gift, a share of a price already cut): no PRICES */
export function localAmount(s: LifeState, n: number, to = 500): number { return local(s, n, to); }
function local(s: LifeState, n: number, to = 500): number {
  const k = COST[hereCountry(s)];
  if (k === 1) return Math.round(n / to) * to;
  const rate = s.card.currency.rate ?? 1, v = (n * k) / rate;
  let step = 10 ** Math.max(0, Math.floor(Math.log10(v)) - 1);
  if (v / step > 50) step *= 5;
  return Math.round(v / step) * step * rate;
}

/**
 * Who you are, and what the world puts in front of you because of it.
 *
 * Personality is gravity, not a railroad: traits never decide what you're allowed to do, they
 * weigh which opportunities turn up (a kind, disciplined person gets asked for help and offered
 * extra shifts; a reckless, broke one gets offered a car to drive somewhere tonight). The
 * generator weighs four things together: base personality, how you are right now (broke,
 * desperate, tired), your circumstances (no job, student, kids, rent due, a business), and what
 * you've done before (help people and they come to you; take one shady job and they come back
 * with bigger ones).
 *
 * And personality drifts with what you actually do: every choice nudges a lean along its axis.
 * Kind, then exploited enough times, turns selfish; reckless, then arrested, can turn cautious.
 */

const DAY = 1440;
const clamp = (v: number) => Math.max(0, Math.min(100, v));
const naira = (s: LifeState, n: number) => money(s, n);
const rngFor = (s: LifeState, salt: number) => new Rng((s.seed ^ Math.imul(salt + 13, 0xc2b2ae35)) >>> 0);
const round = (n: number, to = 500) => Math.round(n / to) * to;

export interface Deeds { helped: number; exploited: number; crimes: number; caught: number; risks: number; lies: number; diligent: number; refused: number }
export interface SelfState {
  /** per axis (keyed by its first pole): +1 = fully the first pole, -1 = fully the second */
  lean: Record<string, number>;
  deeds: Deeds;
  /** opportunities offered recently (def id → day), so the same one doesn't repeat every day */
  recent: Record<string, number>;
  /** someone put in a word for you: applications land more often until then */
  referralUntil?: number;
}

export interface Chance {
  id: string;
  def: string;
  from: string;
  text: string;
  at: number;
  until: number;
  data: Record<string, number | string>;
  choices: { id: string; label: string; hint?: string }[];
  /** helpful / risky / shady / ambitious: what kind of opportunity this is (for the UI and objectives) */
  tone: 'help' | 'risk' | 'shady' | 'ambition' | 'social';
}

/** Something that happens later because of a choice made now. */
export interface Later { at: number; kind: string; data: Record<string, number | string> }
export interface Debt { to: string; amount: number; due: number; warned: number }

// --- personality -----------------------------------------------------------------------------------

const axisOf = (trait: string) => TRAIT_AXES.find((a) => a.includes(trait));

export function selfOf(s: LifeState): SelfState {
  if (!s.self) {
    const r = rngFor(s, 1);
    const lean: Record<string, number> = {};
    for (const [a, b] of TRAIT_AXES) lean[a] = s.card.personality.includes(a) ? r.range(0.5, 0.75) : s.card.personality.includes(b) ? -r.range(0.5, 0.75) : r.range(-0.18, 0.18);
    s.self = { lean, deeds: { helped: 0, exploited: 0, crimes: 0, caught: 0, risks: 0, lies: 0, diligent: 0, refused: 0 }, recent: {} };
  }
  return s.self;
}

/** Who you are now: the (up to three) strongest leans. */
export function traitsOf(s: LifeState): string[] {
  const me = selfOf(s);
  return TRAIT_AXES.map(([a, b]) => ({ t: (me.lean[a] ?? 0) >= 0 ? a : b, v: Math.abs(me.lean[a] ?? 0) }))
    .filter((x) => x.v >= 0.3).sort((x, y) => y.v - x.v).slice(0, 3).map((x) => x.t);
}

const BECAME: Record<string, string> = {
  kind: 'People have started saying you\'re kind. More of them come to you when they need help.',
  selfish: 'Somewhere along the way you stopped giving things away. You look after yourself first now.',
  honest: 'People know your word is good.',
  cunning: 'You\'ve learned to work an angle.',
  reckless: 'You\'ve got a taste for risk now.',
  cautious: 'You think twice before you jump these days.',
  disciplined: 'You\'ve become someone who does what they said they\'d do.',
  impulsive: 'You do things first and think about them later.',
  ambitious: 'You want more than this, and it shows.',
  easygoing: 'You\'ve stopped chasing everything.',
  outgoing: 'You know half the street by name now.',
  introverted: 'You keep more to yourself than you used to.',
  creative: 'You see things other people walk past.',
  practical: 'You fix things instead of talking about them.',
};

/** What you did moves who you are, a little. Says so when it tips you into (or out of) a trait. */
export function nudge(s: LifeState, trait: string, amount: number, events?: LifeEvent[]) {
  const axis = axisOf(trait);
  if (!axis) return;
  const me = selfOf(s);
  const before = traitsOf(s);
  const sign = axis[0] === trait ? 1 : -1;
  me.lean[axis[0]] = Math.max(-1, Math.min(1, (me.lean[axis[0]] ?? 0) + sign * amount));
  const after = traitsOf(s);
  for (const t of after.filter((x) => !before.includes(x))) {
    s.history.push({ t: s.minutes, text: `Became ${t}.` });
    events?.push({ t: s.minutes, kind: 'info', text: BECAME[t] ?? `You've become ${t}.` });
  }
}

// --- what the world offers -----------------------------------------------------------------------

interface Ctx {
  s: LifeState;
  r: Rng;
  t: Set<string>;
  broke: boolean;
  desperate: boolean;
  unemployed: boolean;
  student: boolean;
  rentSoon: boolean;
  kids: boolean;
  me: SelfState;
}

interface Outcome { title: string; lines: string[]; minutes?: number; asleep?: boolean; scene?: string }

interface ChanceDef {
  id: string;
  tone: Chance['tone'];
  /** 0 = can't happen to this life right now */
  weight(c: Ctx): number;
  /** how traits pull it towards you (multipliers) */
  pull: Partial<Record<string, number>>;
  make(c: Ctx): { from: string; text: string; data?: Chance['data']; hours?: number; choices: Chance['choices'] };
  resolve(s: LifeState, choice: string, data: Chance['data'], r: Rng, ev: LifeEvent[]): Outcome;
}

const someone = (c: Ctx, sex?: 'm' | 'f') => randomName(content(c.s), sex ?? (c.r.chance(0.5) ? 'm' : 'f'), c.r);
const friend = (c: Ctx) => c.s.contacts.filter((p) => p.relation === 'Friend' || p.relation === 'Acquaintance').sort((a, b) => b.closeness - a.closeness)[0];
const first = (n: string) => n.split(' ')[0];

/** Busted: taken in. The cell, and the way out, are a scene now (custody.ts): bail is set, not taken. */
const STATION: Record<Country, string> = { nigeria: 'Divisional Police Station, Ikeja', france: 'Commissariat du 11e arrondissement', usa: 'the 9th Precinct', china: 'the district public security bureau', japan: 'the Shibuya police station', italy: 'the Carabinieri station, Piazza Pitti', uae: 'Bur Dubai police station' };
function arrested(s: LifeState, ev: LifeEvent[], what: string): string[] {
  const me = selfOf(s);
  const before = me.deeds.caught++;
  const c = takeIntoCustody(s, what, STATION[hereCountry(s)], before >= 1);
  s.needs.energy = clamp(s.needs.energy - 30);
  s.health = clamp(s.health - 5);
  const lines = [F(s).police.replace(/ You spent the night[^.]*\./, '').trim(), `You're in a cell at ${c.station}. Bail is ${naira(s, c.bail)}.`];
  if (before >= 1) {
    // they know your name now: charged, a lawyer, and word gets to work
    // (a fixed fee where you are, not a share of what you have: a share had no ceiling, and at a fortune it was most of it)
    const lawyer = Math.min(s.money, localPrice(s, 450_000, 10_000));
    spend(s, lawyer, 'Lawyer');
    lines.push(`This time they charged you. The lawyer cost ${naira(s, lawyer)}.`);
    if (s.job) {
      lines.push(`Word got to ${s.job.place}. You don't have a job any more.`);
      s.history.push({ t: s.minutes, text: `Lost the job as ${s.job.label.toLowerCase()} after a criminal charge.` });
      s.job = null;
      s.work.owed = 0;
    }
  }
  void ev;
  return lines;
}
/** Police heat: every job you've done makes the next one likelier to go wrong. */
const heat = (me: SelfState) => Math.min(0.45, me.deeds.crimes * 0.05 + me.deeds.caught * 0.06);

const DEFS: ChanceDef[] = [
  // --- helping -----------------------------------------------------------------------------------
  {
    id: 'move_furniture', tone: 'help', pull: { kind: 2.5, outgoing: 1.3, disciplined: 1.2, selfish: 0.5 },
    weight: (c) => 1 + (c.broke ? 0.5 : 0),
    make: (c) => {
      const n = someone(c, 'm'), pay = local(c.s, c.r.range(6_000, 15_000));
      return { from: n, text: `${first(n)}, a neighbour, is moving this evening and asks if you can help carry furniture. He'll give you ${naira(c.s, pay)} for your trouble.`, data: { pay, name: n }, hours: 10,
        choices: [{ id: 'help', label: 'Help him', hint: 'About 3 hours of carrying' }, { id: 'no', label: 'Make an excuse' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      if (ch !== 'help') { nudge(s, 'selfish', 0.03, ev); return { title: 'Not tonight', lines: ['You tell him you\'re busy. He finds someone else.'] }; }
      earn(s, Number(d.pay), `Helped ${first(String(d.name))} move`);
      s.needs.energy = clamp(s.needs.energy - 25); s.needs.social = clamp(s.needs.social + 18);
      s.skills.fitness = Math.min(100, s.skills.fitness + 0.6);
      s.contacts.push({ name: String(d.name), relation: 'Neighbour', closeness: 40, met: s.minutes });
      selfOf(s).deeds.helped++;
      nudge(s, 'kind', 0.07, ev);
      ev.push(...progress(s, 'help'));
      return { title: 'Moving day', lines: [r.pick(['A wardrobe that would not fit through the door, three trips down the stairs, and cold Malt at the end.', 'You carry a fridge, two mattresses and what feels like a hundred bags of clothes.']), `${first(String(d.name))} pays you ${naira(s, Number(d.pay))} and says to call if you ever need anything.`], minutes: 180, scene: 'carry' };
    },
  },
  {
    id: 'lend_rent', tone: 'help', pull: { kind: 3, selfish: 0.3, cautious: 0.7 },
    weight: (c) => (c.s.job && c.s.money > 60_000 ? 1 : 0),
    make: (c) => {
      const n = someone(c), amt = Math.min(round(c.s.money * 0.3, 1000), local(c.s, c.r.range(25_000, 80_000), 1000));
      return { from: n, text: `${first(n)} from work is short on rent and asks to borrow ${naira(c.s, amt)}. "I'll pay you back at month end, I swear."`, data: { amt, name: n }, hours: 30,
        choices: [{ id: 'lend', label: `Lend ${naira(c.s, amt)}` }, { id: 'no', label: 'Say you can\'t' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const name = first(String(d.name));
      if (ch !== 'lend') { nudge(s, 'selfish', 0.03, ev); return { title: name, lines: [`"I understand," ${name} says, not quite looking at you.`] }; }
      if (!spend(s, Number(d.amt), `Lent to ${name}`)) return { title: name, lines: ['You don\'t actually have it to give.'] };
      selfOf(s).deeds.helped++;
      nudge(s, 'kind', 0.05, ev);
      ev.push(...progress(s, 'help'));
      s.later.push({ at: s.minutes + r.int(8, 16) * DAY + r.int(9, 19) * 60, kind: r.chance(0.68) ? 'repay' : 'no_repay', data: { name: String(d.name), amt: Number(d.amt) } });
      return { title: name, lines: [`You send it. ${name} nearly cries. ${F(s).blessing}`] };
    },
  },
  {
    id: 'charity', tone: 'help', pull: { kind: 2, outgoing: 2, disciplined: 1.2, selfish: 0.4 },
    weight: (c) => 0.7 + (c.me.deeds.helped > 2 ? 0.8 : 0),
    make: (c) => ({ from: F(c.s).charity[0], text: F(c.s).charity[1], hours: 48,
      choices: [{ id: 'yes', label: 'Volunteer', hint: 'A morning, unpaid' }, { id: 'no', label: 'Decline' }] }),
    resolve: (s, ch, _d, r, ev) => {
      if (ch !== 'yes') return { title: 'Food drive', lines: ['You say you can\'t this time.'] };
      s.needs.social = clamp(s.needs.social + 25); s.needs.fun = clamp(s.needs.fun + 10); s.needs.energy = clamp(s.needs.energy - 12);
      s.skills.social = Math.min(100, s.skills.social + 1);
      selfOf(s).deeds.helped += 2;
      nudge(s, 'kind', 0.06, ev); nudge(s, 'outgoing', 0.04, ev);
      ev.push(...progress(s, 'help'));
      if (r.chance(0.35)) s.later.push({ at: s.minutes + r.int(2, 6) * DAY + 11 * 60, kind: 'referral', data: { from: F(s).charity[0] } });
      return { title: 'Food drive', lines: [`You run the line, sort the bags, settle two arguments about who came first. ${F(s).charity[2]}`, `${F(s).charity[0]} watches you all morning.`], minutes: 240, scene: 'crowd' };
    },
  },
  {
    id: 'tutor', tone: 'help', pull: { kind: 2, disciplined: 1.5, introverted: 1.2, selfish: 0.5 },
    weight: (c) => 0.6 + (c.s.skills.academics > 30 ? 0.6 : 0),
    make: (c) => {
      const n = someone(c, 'f');
      return { from: n, text: `${first(n)} from down the road says her son has exams next week and he’s stuck on his maths. Could you sit with him for an afternoon?`, data: { name: n }, hours: 30,
        choices: [{ id: 'yes', label: 'Tutor him', hint: 'An afternoon, unpaid' }, { id: 'no', label: 'Not this week' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const name = first(String(d.name));
      if (ch !== 'yes') return { title: 'Tutoring', lines: ['You say you can’t this week.'] };
      s.needs.social = clamp(s.needs.social + 15); s.needs.energy = clamp(s.needs.energy - 10);
      s.skills.academics = Math.min(100, s.skills.academics + 0.8);
      selfOf(s).deeds.helped++;
      nudge(s, 'kind', 0.05, ev); nudge(s, 'disciplined', 0.03, ev);
      ev.push(...progress(s, 'help'));
      if (!s.contacts.some((c) => c.name === String(d.name))) s.contacts.push({ name: String(d.name), relation: 'Neighbour', closeness: 35, met: s.minutes });
      return { title: 'Tutoring', lines: [r.pick(['Fractions first, then long division. By the end of the afternoon he’s doing them before you ask.', 'You go through last year’s paper with him, question by question, until the answers stop being guesses.']), `${name} sends a plate of food home with you.`], minutes: 180 };
    },
  },
  // --- ambition ----------------------------------------------------------------------------------
  {
    id: 'weekend_project', tone: 'ambition', pull: { disciplined: 2, ambitious: 2, easygoing: 0.5, impulsive: 0.6 },
    weight: (c) => (c.s.job && c.s.work.performance >= 65 && c.s.work.worked >= 4 ? 1.2 : 0),
    make: (c) => {
      const pay = round((c.s.job!.incomeMonthly / 22) * c.r.range(1.5, 2.5), 1000);
      return { from: 'Your manager', text: `"You've been reliable. There's a weekend job that needs doing properly. ${naira(c.s, pay)} extra, Saturday."`, data: { pay }, hours: 30,
        choices: [{ id: 'yes', label: 'Take it', hint: 'Most of Saturday' }, { id: 'no', label: 'Pass' }] };
    },
    resolve: (s, ch, d, _r, ev) => {
      if (ch !== 'yes') return { title: 'Weekend job', lines: ['"No problem." Someone else gets it.'] };
      earn(s, Number(d.pay), 'Weekend work');
      s.work.performance = clamp(s.work.performance + 6);
      s.needs.energy = clamp(s.needs.energy - 20);
      selfOf(s).deeds.diligent++;
      nudge(s, 'disciplined', 0.05, ev); nudge(s, 'ambitious', 0.04, ev);
      return { title: 'Weekend job', lines: ['A long Saturday. The kind of work nobody notices unless it\'s done badly.', `Your manager notices. ${naira(s, Number(d.pay))} lands on Monday.`], minutes: 360, scene: 'work' };
    },
  },
  {
    id: 'side_job', tone: 'ambition', pull: { ambitious: 2.2, practical: 1.6, creative: 1.3, easygoing: 0.6 },
    weight: () => 1,
    make: (c) => {
      const sk = c.s.skills;
      const best = (['technical', 'creativity', 'cooking', 'music', 'academics'] as const).reduce((a, b) => (sk[b] > sk[a] ? b : a));
      const job = F(c.s).sideJobs[best];
      const pay = local(c.s, 4_000 + sk[best] * c.r.range(180, 420), 1000);
      const n = someone(c);
      return { from: n, text: `${first(n)} heard you're good at this: can you ${job} this weekend? ${naira(c.s, pay)}.`, data: { pay, skill: best, job }, hours: 36,
        choices: [{ id: 'yes', label: 'Do it' }, { id: 'no', label: 'Not this time' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      if (ch !== 'yes') return { title: 'Side job', lines: ['You let it go.'] };
      const skill = String(d.skill) as keyof LifeState['skills'];
      const good = r.chance(0.35 + s.skills[skill] / 140);
      const pay = good ? Number(d.pay) : round(Number(d.pay) * 0.6);
      earn(s, pay, 'Side job');
      s.skills[skill] = Math.min(100, s.skills[skill] + 1.2);
      nudge(s, 'ambitious', 0.04, ev);
      return { title: 'Side job', lines: [good ? `You ${d.job}. They're pleased, and they'll tell people.` : `You ${d.job}, more or less. They pay, but less than they said.`, `+${naira(s, pay)}`], minutes: 240, scene: 'work' };
    },
  },
  // --- risk and shade ----------------------------------------------------------------------------
  {
    id: 'package', tone: 'shady', pull: { reckless: 3, cunning: 2, kind: 0.6, cautious: 0.25, honest: 0.3 },
    weight: (c) => (c.broke ? 1.4 : 0.35) * (c.desperate ? 2.5 : 1) * (1 + c.me.deeds.crimes * 0.8),
    make: (c) => {
      const pay = local(c.s, c.r.range(50_000, 110_000) * (1 + Math.min(3, c.me.deeds.crimes) * 0.3), 5000);
      return { from: F(c.s).packageFrom, text: `${F(c.s).packageWho} offers you ${naira(c.s, pay)} to take a sealed package to ${F(c.s).packageTo} tonight. "No questions. You don't open it."`, data: { pay }, hours: 8,
        choices: [{ id: 'do', label: 'Take the package', hint: 'Fast money. Risky.' }, { id: 'no', label: 'Walk away' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const me = selfOf(s);
      if (ch !== 'do') { me.deeds.refused++; nudge(s, 'honest', 0.03, ev); nudge(s, 'cautious', 0.02, ev); return { title: 'No', lines: ['"Your loss," he says, and finds someone else before you\'ve crossed the road.'] }; }
      me.deeds.crimes++; me.deeds.risks++;
      nudge(s, 'reckless', 0.07, ev); nudge(s, 'cunning', 0.06, ev);
      const roll = r.next();
      const caughtP = 0.15 + heat(me) - (s.skills.social / 1000);
      if (roll < caughtP) return { title: 'Checkpoint', lines: arrested(s, ev, 'carrying a package for someone'), minutes: 12 * 60, asleep: true, scene: 'night' };
      if (roll < caughtP + 0.07) {
        s.health = clamp(s.health - 15);
        return { title: 'Robbed', lines: [`${F(s).packageRobbed} They took the package and your phone money.`, 'Nobody pays you for a package you didn\'t deliver.'], minutes: 180, scene: 'night' };
      }
      earn(s, Number(d.pay), 'Delivery (cash)');
      s.needs.fun = clamp(s.needs.fun + 12);
      return { title: 'Delivered', lines: [`${F(s).packageDrop}, a man who doesn't say a word, a thick envelope.`, `${naira(s, Number(d.pay))}. ${F(s).packageWho.replace(/^A /, 'The ').replace(/ you've seen /, ' ')} nods at you the next day. He'll be back.`], minutes: 150, scene: 'night' };
    },
  },
  {
    id: 'drive_car', tone: 'shady', pull: { reckless: 3, cunning: 1.6, cautious: 0.2, honest: 0.4 },
    weight: (c) => (c.desperate ? 1.6 : c.broke ? 0.6 : 0.12) * (1 + c.me.deeds.crimes * 0.7),
    make: (c) => {
      const pay = local(c.s, c.r.range(150_000, 260_000), 10_000);
      return { from: 'A stranger', text: `A stranger offers you ${naira(c.s, pay)} to drive a car ${F(c.s).carRoute}. The papers are "in the glovebox". He won't say whose it is.`, data: { pay }, hours: 6,
        choices: [{ id: 'do', label: 'Drive it', hint: 'Big money. Big risk.' }, { id: 'no', label: 'Refuse' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const me = selfOf(s);
      if (ch !== 'do') { me.deeds.refused++; nudge(s, 'cautious', 0.04, ev); return { title: 'No', lines: ['You shake your head. He shrugs, as if it never happened.'] }; }
      me.deeds.crimes++; me.deeds.risks += 2;
      nudge(s, 'reckless', 0.1, ev);
      if (r.chance(0.28 + heat(me))) {
        const lines = arrested(s, ev, 'driving a stolen car');
        // a stolen car is worse than a package: a missed day of work too
        return { title: F(s).carRoad.replace(/^the /, '').replace(/^./, (x) => x.toUpperCase()), lines: [...lines, 'The car was stolen. It takes your family two days to get you out.'], minutes: 36 * 60, scene: 'night' };
      }
      earn(s, Number(d.pay), 'Drove a car (cash)');
      s.needs.fun = clamp(s.needs.fun + 20); s.needs.energy = clamp(s.needs.energy - 30);
      return { title: 'Delivered', lines: [F(s).carDone, `${naira(s, Number(d.pay))} in cash, counted twice.`], minutes: 300, scene: 'night' };
    },
  },
  {
    id: 'open_till', tone: 'shady', pull: { cunning: 2.2, reckless: 2, impulsive: 1.6, honest: 0.25, kind: 0.6 },
    weight: (c) => (c.broke ? 1 : 0.3),
    make: (c) => ({ from: F(c.s).shop, text: `${F(c.s).shop} owner has stepped out back. The till drawer is open, a fat stack of notes on top. Nobody is looking.`, hours: 1,
      choices: [{ id: 'take', label: 'Take some' }, { id: 'call', label: 'Call out to the owner' }, { id: 'leave', label: 'Leave it' }] }),
    resolve: (s, ch, _d, r, ev) => {
      const me = selfOf(s);
      if (ch === 'call') {
        me.deeds.helped++; nudge(s, 'honest', 0.07, ev); nudge(s, 'kind', 0.03, ev);
        s.contacts.push({ name: 'Mama Tolu', relation: 'Shop owner', closeness: 35, met: s.minutes });
        ev.push(...progress(s, 'help'));
        return { title: 'Honest', lines: ['"Madam, your drawer!" She comes running, counts it, and looks at you for a long second.', '"God bless you. Not everybody would." She won\'t let you pay for your Malt.'] };
      }
      if (ch !== 'take') return { title: 'Not your business', lines: ['You walk on.'] };
      me.deeds.crimes++;
      nudge(s, 'cunning', 0.06, ev); nudge(s, 'honest', -0.08, ev);
      if (r.chance(0.28)) {
        s.health = clamp(s.health - 12);
        s.history.push({ t: s.minutes, text: 'Caught stealing from a shop.' });
        return { title: 'Ole!', lines: ['A boy across the road shouts "Ole!" and the whole street turns.', 'You get your ear boxed and the money taken back, and now people here know your face.'] };
      }
      const took = local(s, r.range(4_000, 18_000));
      earn(s, took, 'Took from a till');
      return { title: 'Gone', lines: [`${naira(s, took)}. Your hands are shaking all the way down the street.`] };
    },
  },
  {
    id: 'fight', tone: 'risk', pull: { reckless: 3, impulsive: 2, cautious: 0.3, easygoing: 0.5 },
    weight: () => 0.6,
    make: (c) => ({ from: F(c.s).fightAt, text: `${F(c.s).fightWho} says you bumped him on purpose. He squares up. His friends are laughing.`, hours: 1,
      choices: [{ id: 'fight', label: 'Square up' }, { id: 'talk', label: 'Talk him down' }, { id: 'walk', label: 'Walk away' }] }),
    resolve: (s, ch, _d, r, ev) => {
      const me = selfOf(s);
      if (ch === 'walk') { nudge(s, 'cautious', 0.03, ev); return { title: 'Walked away', lines: ['They jeer at your back. Your pride hurts more than your face would have.'] }; }
      if (ch === 'talk') {
        const ok = r.chance(0.3 + s.skills.social / 120);
        s.skills.social = Math.min(100, s.skills.social + 0.5);
        return ok ? { title: 'Talked down', lines: ['You make a joke at your own expense. He laughs despite himself. Somebody buys you both a drink.'] }
          : { title: 'Shoved', lines: ['He isn\'t interested in talking. A shove, a stumble, and his friends pull him back.'] };
      }
      me.deeds.risks++;
      nudge(s, 'reckless', 0.05, ev);
      const win = r.chance(0.25 + s.skills.fitness / 110);
      if (win) { s.needs.fun = clamp(s.needs.fun + 15); s.health = clamp(s.health - 6); return { title: 'Won', lines: ['It\'s over fast. He\'s on the ground and his friends stop laughing.', 'The street will talk about this.'] }; }
      s.health = clamp(s.health - 22); s.needs.fun = clamp(s.needs.fun - 10);
      return { title: 'Lost', lines: ['He was bigger than he looked. You wake up on a bench with a split lip and a ringing ear.'], minutes: 60 };
    },
  },
  {
    id: 'big_stake', tone: 'risk', pull: { impulsive: 3, reckless: 2, disciplined: 0.3, cautious: 0.35 },
    weight: (c) => (c.s.money > 20_000 ? 0.8 + (c.rentSoon ? 0.8 : 0) : 0),
    make: (c) => {
      const stake = round(Math.min(c.s.money * 0.5, c.rentSoon && c.s.rent ? c.s.rent.yearly * 0.4 : local(c.s, c.r.range(20_000, 80_000), 1000)), 1000);
      return { from: F(c.s).betFrom, text: `${F(c.s).betLine(naira(c.s, stake))}${c.rentSoon ? ' Your rent money is sitting right there.' : ''}`, data: { stake }, hours: 6,
        choices: [{ id: 'all', label: `Stake ${naira(c.s, stake)}` }, { id: 'small', label: 'Stake a little' }, { id: 'no', label: 'Leave it' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      if (ch === 'no') { nudge(s, 'disciplined', 0.03, ev); return { title: 'Not tonight', lines: ['You watch the game anyway, with your money in your pocket.'] }; }
      const stake = ch === 'all' ? Number(d.stake) : round(Number(d.stake) * 0.15);
      if (!spend(s, stake, 'Bet')) return { title: 'Bet', lines: ['You don\'t have it.'] };
      selfOf(s).deeds.risks++;
      nudge(s, 'impulsive', ch === 'all' ? 0.08 : 0.02, ev); nudge(s, 'reckless', ch === 'all' ? 0.05 : 0.01, ev);
      if (r.chance(0.33)) { const win = stake * 3; earn(s, win, 'Bet (won)'); s.needs.fun = clamp(s.needs.fun + 25); return { title: 'It came in', lines: [`${F(s).betWin} ${naira(s, win)}. You're shouting in the street.`], minutes: 120, scene: 'viewing' }; }
      s.needs.fun = clamp(s.needs.fun - 15);
      return { title: 'One leg cut', lines: [`They drew. ${naira(s, stake)} gone.`, ...(ch === 'all' && s.rent ? ['Rent day is still coming.'] : [])], minutes: 120, scene: 'viewing' };
    },
  },
  {
    id: 'lie_for_friend', tone: 'social', pull: { cunning: 2, kind: 1.4, honest: 0.4 },
    weight: (c) => (friend(c) ? 0.7 : 0),
    make: (c) => {
      const f = friend(c)!;
      return { from: first(f.name), text: `"If my girlfriend calls you, tell her I was with you last night.${F(c.s).please} I'll explain later."`, data: { name: f.name }, hours: 20,
        choices: [{ id: 'lie', label: 'Cover for them' }, { id: 'no', label: 'Refuse to lie' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const f = s.contacts.find((p) => p.name === d.name);
      if (ch !== 'lie') { if (f) f.closeness = Math.max(0, f.closeness - 10); nudge(s, 'honest', 0.05, ev); return { title: first(String(d.name)), lines: ['"Wow. Okay." They don\'t message you for a while.'] }; }
      if (f) f.closeness = Math.min(100, f.closeness + 12);
      selfOf(s).deeds.lies++;
      nudge(s, 'cunning', 0.05, ev); nudge(s, 'honest', -0.04, ev);
      if (r.chance(0.3)) s.later.push({ at: s.minutes + r.int(2, 6) * DAY + 18 * 60, kind: 'lie_found', data: { name: String(d.name) } });
      return { title: first(String(d.name)), lines: [`She calls. You say it smoothly. ${F(s).lieLine} She believes you, for now.`] };
    },
  },
  {
    id: 'big_party', tone: 'social', pull: { outgoing: 2.2, reckless: 1.8, impulsive: 1.3, introverted: 0.4, cautious: 0.6 },
    weight: (c) => (hourOf(c.s.minutes) < 20 ? 0.7 : 0.5),
    make: (c) => ({ from: first(friend(c)?.name ?? 'Tunde'), text: F(c.s).party[1], hours: 10,
      choices: [{ id: 'go', label: 'Go', hint: 'Transport and drinks' }, { id: 'no', label: 'Stay in' }] }),
    resolve: (s, ch, _d, r, ev) => {
      if (ch !== 'go') return { title: 'Stayed in', lines: ['You hear about it for days.'] };
      const cost = Math.min(s.money, localPrice(s, r.range(8_000, 20_000)));
      spend(s, cost, 'Party');
      s.needs.fun = clamp(s.needs.fun + 40); s.needs.social = clamp(s.needs.social + 35); s.needs.energy = clamp(s.needs.energy - 25);
      nudge(s, 'outgoing', 0.05, ev);
      const n = randomName(content(s), r.chance(0.5) ? 'm' : 'f', r);
      s.contacts.push({ name: n, relation: 'Acquaintance', closeness: 25, met: s.minutes });
      if (r.chance(0.35)) { s.later.push({ at: s.minutes + r.int(1, 4) * DAY + 15 * 60, kind: 'circle', data: { name: n } }); }
      return { title: F(s).party[0].replace(/^a /, '').replace(/^./, (x) => x.toUpperCase()), lines: [`${F(s).party[2]}, and ${first(n)}, who seems to know everyone.`, `You spent ${naira(s, cost)}. Worth it, probably.`], minutes: 300, scene: 'party' };
    },
  },
  // --- at work: the job has its own temptations ------------------------------------------------------
  {
    id: 'credit_theft', tone: 'ambition', pull: { ambitious: 1.6, honest: 1.3, easygoing: 0.7 },
    weight: (c) => (c.s.job && c.s.work.worked >= 5 ? 0.9 : 0),
    make: (c) => {
      const n = someone(c);
      return { from: first(n), text: `${first(n)} presented your report in the management meeting as their own. Your manager loved it.`, data: { name: n }, hours: 30,
        choices: [{ id: 'confront', label: 'Confront them' }, { id: 'boss', label: 'Quietly show your manager the drafts' }, { id: 'let', label: 'Let it go' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const n = first(String(d.name));
      if (ch === 'boss') {
        if (r.chance(0.45 + s.skills.social / 200)) { s.work.performance = clamp(s.work.performance + 10); nudge(s, 'cunning', 0.02, ev); return { title: 'Credit where it\'s due', lines: ['Your manager looks at the timestamps for a long moment. "Leave it with me." The next meeting, she thanks you by name.'] }; }
        s.work.performance = clamp(s.work.performance - 4);
        return { title: 'Office politics', lines: [`"I don\'t have time for this." She thinks you\'re the problem now. ${n} smiles at you in the corridor.`] };
      }
      if (ch === 'confront') { nudge(s, 'honest', 0.04, ev); s.needs.social = clamp(s.needs.social - 8); return { title: n, lines: [`${n} goes red, then defensive, then avoids you for a week. But they never do it again.`] }; }
      nudge(s, 'easygoing', 0.04, ev); s.work.performance = clamp(s.work.performance - 3);
      return { title: 'Let it go', lines: ['You let it go. It eats at you for a day or two.'] };
    },
  },
  {
    id: 'backdate', tone: 'shady', pull: { cunning: 2, honest: 0.3, cautious: 0.5 },
    weight: (c) => (c.s.job && /accountant|banker|civil|engineer|intern/.test(c.s.job.id) ? 0.8 : 0),
    make: () => ({ from: 'Your manager', text: '"I need you to backdate these invoices to last quarter. It\'s just paperwork. Everyone does it. I\'ll remember who helped."', hours: 20,
      choices: [{ id: 'do', label: 'Do it' }, { id: 'refuse', label: 'Refuse politely' }, { id: 'report', label: 'Report it to compliance' }] }),
    resolve: (s, ch, _d, r, ev) => {
      const me = selfOf(s);
      if (ch === 'do') {
        me.deeds.lies++; nudge(s, 'cunning', 0.06, ev); nudge(s, 'honest', -0.06, ev);
        s.work.performance = clamp(s.work.performance + 8);
        if (r.chance(0.18)) s.later.push({ at: s.minutes + r.int(10, 30) * DAY, kind: 'audit', data: {} });
        return { title: 'Paperwork', lines: ['Twenty minutes and a pen. Your manager pats your shoulder: "This is why I like you."'] };
      }
      if (ch === 'report') {
        nudge(s, 'honest', 0.08, ev);
        if (r.chance(0.5)) { s.work.performance = clamp(s.work.performance + 5); return { title: 'Compliance', lines: ['Compliance thanks you and opens a file. Your manager is moved to another branch within the month.'] }; }
        s.work.performance = clamp(s.work.performance - 15);
        return { title: 'Compliance', lines: ['Compliance "looks into it". Your manager finds out who reported. Your next few weeks are very long.'] };
      }
      nudge(s, 'honest', 0.04, ev); s.work.performance = clamp(s.work.performance - 5);
      return { title: 'No', lines: ['"Fine. I\'ll find someone who understands how things work." He doesn\'t look at you the rest of the day.'] };
    },
  },
  {
    id: 'envelope', tone: 'shady', pull: { cunning: 1.8, selfish: 1.5, honest: 0.3 },
    weight: (c) => (c.s.job && /banker|civil|nurse|doctor|lawyer|engineer|teacher/.test(c.s.job.id) ? 0.7 : 0),
    make: (c) => {
      const amt = local(c.s, c.r.range(50_000, 200_000), 10_000);
      return { from: 'A client', text: `A client leaves an envelope on your desk. "${naira(c.s, amt)}. For your trouble. You'll make sure my file moves quickly${F(c.s).bribeTag}"`, data: { amt }, hours: 8,
        choices: [{ id: 'take', label: 'Take it' }, { id: 'return', label: 'Give it back' }] };
    },
    resolve: (s, ch, d, r, ev) => {
      const me = selfOf(s);
      if (ch !== 'take') { nudge(s, 'honest', 0.05, ev); return { title: 'No thank you', lines: ['You push it back across the desk. The client looks surprised, then almost respectful.'] }; }
      me.deeds.crimes += 0.5; nudge(s, 'cunning', 0.05, ev); nudge(s, 'honest', -0.05, ev);
      earn(s, Number(d.amt), 'Envelope');
      if (r.chance(0.12)) s.later.push({ at: s.minutes + r.int(5, 20) * DAY, kind: 'audit', data: {} });
      return { title: 'The envelope', lines: [`${naira(s, Number(d.amt))}. The file moves quickly.`] };
    },
  },
  // --- desperate ---------------------------------------------------------------------------------
  {
    id: 'loan_shark', tone: 'risk', pull: { impulsive: 1.8, reckless: 1.6, cautious: 0.4, disciplined: 0.5 },
    weight: (c) => (c.desperate && !c.s.debts.length ? 2 : 0),
    make: (c) => {
      const amt = Math.max(local(c.s, 60_000, 10_000), round((c.s.rent?.yearly ?? local(c.s, 150_000)) * 0.5, 10_000));
      return { from: F(c.s).lender, text: `A lender in the area will give you ${naira(c.s, amt)} today. You pay back ${naira(c.s, Math.round(amt * 1.5))} in a month. "Everybody pays me. Everybody."`, data: { amt }, hours: 24,
        choices: [{ id: 'take', label: 'Take the loan' }, { id: 'no', label: 'Don\'t' }] };
    },
    resolve: (s, ch, d, _r, ev) => {
      if (ch !== 'take') return { title: 'No loan', lines: ['Something about the way he said "everybody" stays with you.'] };
      earn(s, Number(d.amt), `Loan from ${F(s).lender}`);
      s.debts.push({ to: F(s).lender, amount: Math.round(Number(d.amt) * 1.5), due: dayOf(s.minutes) + 30, warned: 0 });
      nudge(s, 'reckless', 0.03, ev);
      return { title: 'Loan', lines: [`${naira(s, Number(d.amt))} in your account. ${naira(s, Math.round(Number(d.amt) * 1.5))} due in a month.`] };
    },
  },
];

function ctxOf(s: LifeState, r: Rng): Ctx {
  const c = content(s);
  const day = dayOf(s.minutes);
  const broke = s.money < c.prices.transport * 35;
  const rentSoon = !!s.rent && s.rent.dueDay - day <= 21 && s.money < s.rent.yearly;
  return {
    s, r, t: new Set(traitsOf(s)), broke, rentSoon,
    desperate: broke && (rentSoon || s.needs.hunger < 30 || s.debts.some((d) => d.due <= day)),
    unemployed: !s.job && s.card.education.level !== 'student',
    student: s.card.education.level === 'student',
    kids: s.contacts.some((p) => p.relation === 'Son' || p.relation === 'Daughter'),
    me: selfOf(s),
  };
}

/** How strongly this life is drawn to an opportunity: circumstances × personality. */
function weightOf(def: ChanceDef, c: Ctx): number {
  let w = def.weight(c);
  if (w <= 0) return 0;
  for (const [t, m] of Object.entries(def.pull)) if (c.t.has(t)) w *= m!;
  // a reputation pulls more of the same: helpers get asked, the shady get offered
  if (def.tone === 'help') w *= 1 + Math.min(1.5, c.me.deeds.helped * 0.15);
  return w;
}

/** Once a day: 0–2 opportunities, arriving at some hour of the day. */
export function characterDay(s: LifeState, events: LifeEvent[]) {
  const day = dayOf(s.minutes);
  const r = rngFor(s, day * 5 + 3);
  const me = selfOf(s);
  const n = (r.chance(0.8) ? 1 : 0) + (r.chance(0.22) ? 1 : 0);
  for (let i = 0; i < n; i++) {
    const c = ctxOf(s, r);
    const pool = DEFS.filter((d) => !(d.id in me.recent && day - me.recent[d.id] < 5) && !s.chances.some((x) => x.def === d.id))
      .map((d) => [d, weightOf(d, c)] as const).filter(([, w]) => w > 0);
    if (!pool.length) break;
    const def = r.weighted(pool);
    me.recent[def.id] = day;
    const at = day * DAY + r.int(9 * 60, 20 * 60);
    s.later.push({ at, kind: 'chance', data: { def: def.id, salt: r.int(0, 1e9) } });
  }
  // debts: pay when due, or the lender comes round
  for (const d of s.debts) {
    if (d.due > day) continue;
    if (spend(s, d.amount, `Repaid ${d.to}`)) { d.amount = 0; events.push({ t: s.minutes, kind: 'pay', text: `You paid ${d.to} back. You're clear.` }); continue; }
    d.warned++;
    if (d.warned <= 2) message(s, d.to, d.warned === 1 ? 'Your time is up. I want my money.' : 'I am not going to ask again.', events);
    else {
      const took = Math.min(s.money, d.amount);
      spend(s, took, `Taken by ${d.to}'s boys`);
      d.amount -= took;
      s.health = clamp(s.health - 25);
      s.history.push({ t: s.minutes, text: `${d.to}'s boys came for the money.` });
      events.push({ t: s.minutes, kind: 'need', text: `Two of ${d.to}'s boys found you. They took ${naira(s, took)} and left you bruised.` });
      if (d.amount > 0) d.due = day + 7, d.warned = 0;
    }
  }
  s.debts = s.debts.filter((d) => d.amount > 0);
}

/** Called as time passes: opportunities arrive, old ones lapse, and choices come back around. */
export function characterTick(s: LifeState, events: LifeEvent[]) {
  if (s.later.length) {
    const due = s.later.filter((l) => l.at <= s.minutes);
    s.later = s.later.filter((l) => l.at > s.minutes);
    for (const l of due) followUp(s, l, events);
  }
  if (s.chances.length) s.chances = s.chances.filter((c) => c.until > s.minutes);
}

function followUp(s: LifeState, l: Later, events: LifeEvent[]) {
  const d = l.data;
  switch (l.kind) {
    case 'chance': {
      const def = DEFS.find((x) => x.id === d.def);
      if (!def) return;
      const r = new Rng(Number(d.salt) >>> 0);
      const c = ctxOf(s, r);
      if (def.weight(c) <= 0) return; // circumstances changed since it was rolled
      const m = def.make(c);
      const ch: Chance = { id: `c${s.minutes}-${def.id}`, def: def.id, from: m.from, text: m.text, at: s.minutes, until: s.minutes + (m.hours ?? 12) * 60, data: { ...(m.data ?? {}), salt: Number(d.salt) }, choices: m.choices, tone: def.tone };
      s.chances.push(ch);
      events.push({ t: s.minutes, kind: 'chance', text: `${m.from}: ${m.text}` });
      return;
    }
    case 'repay':
      earn(s, Number(d.amt), `${first(String(d.name))} paid you back`);
      { const p = s.contacts.find((x) => x.name === d.name); if (p) p.closeness = Math.min(100, p.closeness + 10); else s.contacts.push({ name: String(d.name), relation: 'Colleague', closeness: 50, met: s.minutes }); }
      events.push({ t: s.minutes, kind: 'pay', text: `${first(String(d.name))} paid you back ${naira(s, Number(d.amt))}, with a thank-you.` });
      return;
    case 'no_repay':
      selfOf(s).deeds.exploited++;
      message(s, String(d.name), 'Please give me small time. Things are hard.', events);
      // kind people who keep getting used start to harden
      if (selfOf(s).deeds.exploited >= 2) nudge(s, 'selfish', 0.12, events); else nudge(s, 'kind', -0.05, events);
      return;
    case 'lie_found': {
      const p = s.contacts.find((x) => x.name === d.name);
      if (p) p.closeness = Math.max(0, p.closeness - 8);
      events.push({ t: s.minutes, kind: 'info', text: `${first(String(d.name))}'s girlfriend found out you lied for him. She's telling everyone.` });
      return;
    }
    case 'referral':
      message(s, String(d.from), 'My brother runs an office in Ikeja and needs someone dependable. I gave him your name. Apply this week.', events);
      selfOf(s).referralUntil = s.minutes + 7 * DAY; // your next application is likelier to land
      return;
    case 'audit': {
      // the paperwork you signed, or the envelope you took, comes back
      if (!s.job) return;
      events.push({ t: s.minutes, kind: 'fired', text: `An internal audit found irregularities with your name on them. You've been dismissed from ${s.job.label.toLowerCase()}.` });
      s.history.push({ t: s.minutes, text: `Dismissed after an audit at ${s.job.place}.` });
      nudge(s, 'cautious', 0.12, events);
      s.job = null; s.work.owed = 0;
      return;
    }
    case 'circle':
      message(s, String(d.name), 'You looked like someone who gets things done. I might have something for you. Call me.', events);
      selfOf(s).deeds.crimes += 0.5; // you're on that radar now: shady offers come more often
      return;
  }
}

/** Your answer to an opportunity: what happens, what it costs in time, what it changes in you. */
export function answerChance(s: LifeState, id: string, choice: string): { title: string; lines: string[]; events: LifeEvent[]; scene?: string } | null {
  const ch = s.chances.find((c) => c.id === id);
  if (!ch) return null;
  const def = DEFS.find((d) => d.id === ch.def);
  s.chances = s.chances.filter((c) => c.id !== id);
  if (!def) return null;
  const ev: LifeEvent[] = [];
  const out = def.resolve(s, choice, ch.data, new Rng((Number(ch.data.salt) ^ 0x51ed) >>> 0), ev);
  if (out.minutes) ev.push(...advance(s, out.minutes, { asleep: out.asleep }));
  return { title: out.title, lines: out.lines, events: ev, scene: out.scene };
}

/** For objectives: how much a trait pulls an everyday goal (1 = neutral). */
export function objectivePull(s: LifeState, kind: string): number {
  const t = new Set(traitsOf(s));
  const PULL: Record<string, Partial<Record<string, number>>> = {
    save: { disciplined: 2, cautious: 1.5, impulsive: 0.5 },
    talk: { outgoing: 2, introverted: 0.5 },
    post: { outgoing: 1.5, creative: 1.3 },
    apply: { ambitious: 2 },
    cook: { practical: 1.5, introverted: 1.3 },
    practice: { creative: 1.6, disciplined: 1.4, introverted: 1.3 },
    explore: { reckless: 1.6, outgoing: 1.3, cautious: 0.7 },
    help: { kind: 3, selfish: 0.3 },
    call: { kind: 1.5 },
    buy_food: { impulsive: 1.4 },
    photo_asked: { outgoing: 1.8, introverted: 0.5 },
  };
  let w = 1;
  for (const [k, m] of Object.entries(PULL[kind] ?? {})) if (t.has(k)) w *= m!;
  return w;
}
